import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Image,
  keyframes,
  Stack,
  Tag,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import { ISelectiveProcess } from '../../../../services/hooks/SelectiveProcess/useRequestSelectiveProcess';
import { badgeColor } from '../../../../utils/badgeFunction';
import { TbFileDescription } from 'react-icons/tb';
import { FiCalendar } from 'react-icons/fi';
import { RiArrowRightSLine, RiMoneyDollarCircleLine } from 'react-icons/ri';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserGroup } from 'react-icons/fa6';
import pcdLogo from '../../../../assets/pcd.svg';

interface IOwnProps {
  index: number;
  data: ISelectiveProcess;
}

const CardSelectiveProcess: React.FC<IOwnProps> = ({ data, index }) => {
  const navigate = useNavigate();

  const floatIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
  `;

  return (
    <Card
      animation={`${floatIn} 0.6s ease ${index * 0.3}s forwards`}
      opacity={0}
      borderRadius="md"
      boxShadow="xl"
      _hover={{ boxShadow: 'md' }}
      transition="0.2s ease"
      bg="#ffffff"
      w="full"
    >
      <CardHeader pb={0}>
        <Stack justify="space-between" align="start">
          <Tag size="sm" variant="outline" colorScheme="purple">
            <TagLabel>{data.type}</TagLabel>
          </Tag>
          {/* <Badge colorScheme="">{data.type}</Badge> */}
          <Heading size="md" fontSize="md" color="gray.800">
            {data.name}
          </Heading>
        </Stack>
      </CardHeader>
      <CardBody pt={0}>
        <Flex gap={1} my={3} flexWrap="wrap">
          {data.selective_process_situation.map((situation) => {
            const colorBadge = badgeColor(situation.name);

            return (
              <Badge
                key={`${situation.name}-${situation.id}`}
                bgColor={colorBadge?.bgColor}
                color={colorBadge?.color}
                px={2}
                py={1}
                borderRadius="md"
                display="flex"
                alignItems="center"
                w="fit-content"
                gap={2}
              >
                <Text fontSize="0.63rem" as="span">
                  {situation.name}
                </Text>
              </Badge>
            );
          })}
        </Flex>
        <Stack spacing={1} fontSize="sm" color="gray.600">
          <HStack>
            <Box w="17px">
              <TbFileDescription size={19} />
            </Box>
            <Text>
              <Text as="span" mr={1} fontWeight="bold">
                Edital:
              </Text>
              {data.public_notice_number}
            </Text>
          </HStack>
          {data.exemption_start && data.exemption_end && (
            <HStack>
              <Box w="17px">
                <RiMoneyDollarCircleLine size={18} />
              </Box>
              <Stack gap={0}>
                <Text as="span" mr={1} fontWeight="bold">
                  Período de isenções
                </Text>
                <Flex>
                  <Box
                    ml={1}
                    borderLeft="1px solid"
                    mt="-2px"
                    mr={2}
                    borderBottom="1px solid"
                    h="12px"
                    w="12px"
                    position="relative"
                  >
                    <Box position="absolute" right="-9px" bottom="-9px">
                      <RiArrowRightSLine size={17} />
                    </Box>
                  </Box>
                  {data.exemption_start.br} até {data.exemption_end.br}
                </Flex>
              </Stack>
            </HStack>
          )}
          {data.registrations_start && data.registrations_end && (
            <HStack>
              <Box w="17px">
                <FiCalendar size={17} />
              </Box>
              <Stack gap={0}>
                <Text as="span" mr={1} fontWeight="bold">
                  Período de inscrições
                </Text>
                <Flex>
                  <Box
                    ml={1}
                    borderLeft="1px solid"
                    mt="-2px"
                    mr={2}
                    borderBottom="1px solid"
                    h="12px"
                    w="12px"
                    position="relative"
                  >
                    <Box position="absolute" right="-9px" bottom="-9px">
                      <RiArrowRightSLine size={17} />
                    </Box>
                  </Box>
                  {data.registrations_start.br} até {data.registrations_end.br}
                </Flex>
              </Stack>
            </HStack>
          )}
          {(data.has_black_or_brown || data.has_pcd) && (
            <HStack align="flex-start" flexDir="column" gap={1}>
              {data.has_pcd && (
                <>
                  <Flex align="center" my={1}>
                    <Image src={pcdLogo} w="18px" h="18px" />
                    <Text ml={2} fontWeight="bold">
                      Pessoas com deficiência (PCDs)
                    </Text>
                  </Flex>
                  <Flex display={['none', 'none', 'flex', 'flex', 'flex']}>|</Flex>
                </>
              )}

              {data.has_black_or_brown && (
                <Flex align="center">
                  <FaUserGroup size={18} />
                  <Text ml={2} fontWeight="bold">
                    Cotas raciais
                  </Text>
                </Flex>
              )}
            </HStack>
          )}
          <Stack spacing={0} w="100%" align="center" mt={4} mb={3}>
            <Box lineHeight={5} mb={2}>
              <Text textAlign="center" fontSize="1.7rem" fontWeight="bold">
                {data.number_of_vacancies || '--'}
              </Text>
              <Text textAlign="center" fontSize="smaller">
                Vagas disponiveis
              </Text>
            </Box>
            <Button
              onClick={() => {
                navigate(`/processo-seletivo/informacoes/${data.id}`);
              }}
              h="auto"
              p={2}
              fontSize="smaller"
              minW="fit-content"
              w="50%"
              bgColor="secondaryColor"
              color="white"
            >
              MAIS INFORMAÇÕES
            </Button>
          </Stack>
        </Stack>
      </CardBody>
    </Card>
  );
};

export default CardSelectiveProcess;
